import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { InteractionStatus } from "@azure/msal-browser";
import { clearTokenCache, startProactiveRefresh, stopProactiveRefresh, updateUserActivity } from "../services/authService.js";
import { clearApplicationDataAndReload } from '../utils/clearApplicationData.js';
import logger from "../utils/logger.js";
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext();
const tokenRequest = {
    scopes: ["User.Read"]
};

const SessionExpiredModal = React.lazy(() =>
    import('cprime-foundational-components').then((module) => ({
        default: module.SessionExpired
    }))
);

const browserRuntime = globalThis;

const buildUserAccount = (msalAccount) => ({
    name: msalAccount?.name,
    role: Array.isArray(msalAccount?.idTokenClaims?.roles) && msalAccount.idTokenClaims.roles.length
        ? msalAccount.idTokenClaims.roles
        : '',
    username: msalAccount?.username,
    idToken: msalAccount?.idToken || ''
});

const persistAuthenticatedUser = (instance, activeMsalAccount, setUser, setIsAuthenticated) => {
    const account = buildUserAccount(activeMsalAccount);
    setUser(account);
    instance.setActiveAccount(activeMsalAccount);
    sessionStorage.setItem("user", JSON.stringify(account));
    sessionStorage.setItem("idToken", account.idToken);
    setIsAuthenticated(true);
    startProactiveRefresh();
};

const shouldWaitForMsalInteraction = (inProgress) => {
    if (inProgress !== InteractionStatus.None) {
        logger.info("Auth in progress...");
        return true;
    }

    return false;
};

const requestLoginRedirect = async (instance, redirectRequestedRef) => {
    if (redirectRequestedRef.current) {
        return;
    }

    redirectRequestedRef.current = true;
    try {
        await instance.loginRedirect(tokenRequest);
    } catch (error) {
        redirectRequestedRef.current = false;
        if (error?.errorCode === 'interaction_in_progress') {
            logger.info('Auth interaction already in progress.');
            return;
        }

        throw error;
    }
};

const finalizeRedirectResult = (instance, redirectRequestedRef, redirectResult) => {
    if (!redirectResult) {
        return;
    }

    instance.setActiveAccount(redirectResult.account);
    redirectRequestedRef.current = false;
};

export const AuthProvider = ({ children }) => {
    const { instance, inProgress } = useMsal();
    const [user, setUser] = useState(() => {
        const savedUser = sessionStorage.getItem("user");
        return savedUser ? JSON.parse(savedUser) : null
    });
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showSessionPopup, setShowSessionPopup] = useState(false);
    const triggerSessionExpiredPopup = () => setShowSessionPopup(true);

    // Guard against React 18 StrictMode double-invoking effects in dev,
    // and prevent multiple interactive MSAL calls (interaction_in_progress).
    const authInitInFlightRef = useRef(false);
    const redirectRequestedRef = useRef(false);

    const initializeAuth = useCallback(async () => {
        if (authInitInFlightRef.current) {
            return;
        }
        authInitInFlightRef.current = true;
        try {
            const redirectResult = await instance.handleRedirectPromise();
            finalizeRedirectResult(instance, redirectRequestedRef, redirectResult);

            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
                persistAuthenticatedUser(instance, accounts[0], setUser, setIsAuthenticated);
                return;
            }

            if (shouldWaitForMsalInteraction(inProgress)) {
                return;
            }

            await requestLoginRedirect(instance, redirectRequestedRef);
        } catch (error) {
            if (error?.errorCode === 'interaction_in_progress') {
                // Allow a later retry once MSAL finishes the current interaction.
                redirectRequestedRef.current = false;
                logger.info('Auth interaction already in progress.');
                return;
            }
            logger.error("Auth initilization  error:", error);
        } finally {
            authInitInFlightRef.current = false;
        }

    }, [inProgress, instance]);

    useEffect(() => {
        initializeAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProgress]);

    // Track user activity for proactive token refresh
    useEffect(() => {
        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

        const handleActivity = () => {
            updateUserActivity();
        };

        // Add listeners for user activity
        activityEvents.forEach(event => {
            browserRuntime.addEventListener(event, handleActivity, { passive: true });
        });

        // Cleanup on unmount
        return () => {
            activityEvents.forEach(event => {
                browserRuntime.removeEventListener(event, handleActivity);
            });
            stopProactiveRefresh();
        };
    }, []);

    const handleLogin = async () => {
        setIsAuthenticated(false);
        if (inProgress !== InteractionStatus.None || redirectRequestedRef.current) {
            return;
        }
        redirectRequestedRef.current = true;
        try {
            await instance.loginRedirect(tokenRequest);
        } catch (error) {
            if (error?.errorCode === 'interaction_in_progress') {
                redirectRequestedRef.current = false;
                return;
            }
            redirectRequestedRef.current = false;
            throw error;
        }
    }

    const logout = useCallback(async () => {
        setUser(null);
        clearTokenCache();
        stopProactiveRefresh();
        await instance.initialize();
        sessionStorage.clear();
        await instance.logoutRedirect();
    }, [instance]);

    const resetApplicationData = useCallback(async () => {
        setUser(null);
        setIsAuthenticated(false);
        await clearApplicationDataAndReload({
            browserRuntime: globalThis,
            msalInstance: instance
        });
    }, [instance]);

    const authContextValue = useMemo(() => ({
        user,
        logout,
        resetApplicationData,
        triggerSessionExpiredPopup,
        isAuthenticated
    }), [isAuthenticated, logout, resetApplicationData, user]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
            {showSessionPopup && (
                <React.Suspense fallback={null}>
                    <SessionExpiredModal
                        title='Session Expired'
                        body={<Typography variant="body2">Your session has expired. Click below to refresh your login and continue.</Typography>}
                        actionLabel="Refresh Login"
                        handleLogIn={handleLogin}
                    />
                </React.Suspense>
            )}
        </AuthContext.Provider>
    )
}
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
};